import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle } from '@phosphor-icons/react';
import api from '@/services/api';

const companyPhone = process.env.REACT_APP_COMPANY_PHONE || '+1 (555) 000-0000';
const calendlyUrl = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com';
const contactName = process.env.REACT_APP_SALES_CONTACT_NAME || 'LotMate Team';
const fallbackEmail = process.env.REACT_APP_SALES_CONTACT_EMAIL || 'hello@yourcompany.com';

export default function AuditThankYouPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const firstName = location.state?.firstName || 'there';
  const amount = location.state?.amount;

  const [supportEmail, setSupportEmail] = useState('');
  const [platformName, setPlatformName] = useState('LotMate');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/public/platform-branding');
        if (cancelled) return;
        if (data?.support_email) setSupportEmail(data.support_email);
        if (data?.platform_name) setPlatformName(data.platform_name);
      } catch {
        /* keep fallbacks */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayEmail = supportEmail || fallbackEmail;

  return (
    <div
      className="min-h-[100dvh] overflow-x-hidden bg-slate-50 px-3 py-6 sm:p-6 md:p-10"
      style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      data-testid="audit-thank-you"
    >
      <div className="max-w-2xl mx-auto space-y-8 text-center min-w-0">
        <div className="flex justify-center">
          <div className="rounded-full bg-gradient-to-br from-cyan-100 to-indigo-100 p-6 animate-in zoom-in duration-500">
            <CheckCircle className="w-20 h-20 text-cyan-600" weight="fill" />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Thank you {firstName}!</h1>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">
            We&apos;ve received your audit results and will be in touch within{' '}
            <span className="text-slate-900 font-semibold">24 hours</span> to show you exactly how {platformName} can recover your{' '}
            {amount != null ? (
              <span className="font-semibold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent">
                ${Number(amount).toLocaleString()}/month
              </span>
            ) : (
              'lost revenue'
            )}{' '}
            in lost revenue.
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">In the meantime</p>
          <div className="max-w-sm mx-auto">
            <Card className="rounded-2xl border-slate-200 bg-white shadow-sm">
              <CardContent className="p-4 space-y-2 text-left">
                <div className="text-2xl">📤</div>
                <div className="font-semibold text-sm text-slate-900">Share with your GM</div>
                <div className="text-xs text-slate-600">Send them your audit results</div>
                <Button variant="outline" className="w-full h-9 text-xs rounded-xl border-slate-200" type="button" onClick={() => navigate('/audit', { state: { share: true } })}>
                  Share Results
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="rounded-2xl border-slate-200 bg-white shadow-sm">
          <CardContent className="p-5 text-left space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Questions? Reach out directly</p>
            <p className="font-semibold text-slate-900">{contactName}</p>
            <p className="text-sm">
              <a href={`mailto:${displayEmail}`} className="text-cyan-700 hover:text-indigo-600 font-medium underline-offset-2 hover:underline">
                {displayEmail}
              </a>
            </p>
            <Button variant="link" className="px-0 h-auto text-cyan-700 hover:text-indigo-600" asChild>
              <a href={calendlyUrl} target="_blank" rel="noreferrer">Book time on my calendar → Calendly</a>
            </Button>
          </CardContent>
        </Card>

        <div className="pt-6 border-t border-slate-200 space-y-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Thank you</p>
          <div className="flex justify-center">
            <img
              src="/logo.svg"
              alt={`${platformName} logo`}
              className="h-16 sm:h-20 w-auto max-w-[min(90vw,20rem)] object-contain object-center mx-auto"
            />
          </div>
          <p className="text-sm text-slate-600">
            <a href={`mailto:${displayEmail}`} className="text-cyan-700 hover:text-indigo-600 font-medium">
              {displayEmail}
            </a>
          </p>
        </div>

        <Link to="/audit" className="text-sm text-slate-600 hover:text-slate-900 underline inline-block">Back to audit</Link>
      </div>
    </div>
  );
}
